import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle, Play, Target, BookOpen, Award } from 'lucide-react'

const LevelGuidePage = () => {
  const { level } = useParams()
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set())

  // Load checked items from localStorage on component mount
  useEffect(() => {
    const savedItems = localStorage.getItem(`level-${level}-progress`)
    if (savedItems) {
      setCheckedItems(new Set(JSON.parse(savedItems)))
    }
  }, [level])

  // Save checked items to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`level-${level}-progress`, JSON.stringify([...checkedItems]))
  }, [checkedItems, level])

  const toggleItem = (itemId: string) => {
    const newCheckedItems = new Set(checkedItems)
    if (newCheckedItems.has(itemId)) {
      newCheckedItems.delete(itemId)
    } else {
      newCheckedItems.add(itemId)
    }
    setCheckedItems(newCheckedItems)
  }

  const levelData = {
    'a1': {
      title: 'A1 Level (Beginner/Breakthrough)',
      description: 'At the A1 level, learners can engage with English in a very basic and concrete manner, focusing on immediate needs and familiar situations.',
      color: 'from-green-400 to-emerald-500',
      topics: [
        {
          title: 'Personal Introductions',
          description: 'Introducing oneself and others',
          items: [
            'Can say my name clearly',
            'Can introduce family members',
            'Can ask someone their name',
            'Can use basic greetings in introductions'
          ]
        },
        {
          title: 'Basic Personal Details',
          description: 'Asking and answering simple questions about where they live, people they know, and things they have',
          items: [
            'Can say where I live',
            'Can ask where someone lives',
            'Can name family members',
            'Can talk about things I own',
            'Can describe basic personal information'
          ]
        },
        {
          title: 'Everyday Expressions',
          description: 'Using familiar everyday phrases and very basic expressions for concrete needs',
          items: [
            'Can use basic courtesy expressions',
            'Can ask for help with simple words',
            'Can express basic needs (hungry, thirsty)',
            'Can say yes/no appropriately',
            'Can use common everyday phrases'
          ]
        },
        {
          title: 'Simple Instructions & Signs',
          description: 'Following basic instructions and understanding common signs, labels, and very simple sentences',
          items: [
            'Can understand simple directional signs',
            'Can follow one-step instructions',
            'Can read basic labels and signs',
            'Can understand simple notices',
            'Can comprehend basic written instructions'
          ]
        },
        {
          title: 'Familiar Places & Objects',
          description: 'Naming rooms, familiar places, and everyday objects, often with pictorial support',
          items: [
            'Can name rooms in a house',
            'Can identify common objects',
            'Can describe familiar places',
            'Can talk about my neighborhood',
            'Can name places in my city'
          ]
        },
        {
          title: 'Short Messages',
          description: 'Understanding and writing very short, simple messages and notes',
          items: [
            'Can write a simple text message',
            'Can understand basic notes',
            'Can write a short personal note',
            'Can read simple messages',
            'Can write basic information about myself'
          ]
        }
      ],
      vocabulary: [
        {
          category: 'Greetings',
          items: [
            'Can use "Hello," "Hi," "Good morning/afternoon/evening"',
            'Can respond appropriately to greetings',
            'Can say goodbye properly',
            'Can use greetings at different times of day'
          ]
        },
        {
          category: 'Basic Courtesy',
          items: [
            'Can say "Please," "Thank you," "Excuse me"',
            'Can apologize with "Sorry"',
            'Can ask permission with "May I?"',
            'Can show politeness in basic situations'
          ]
        },
        {
          category: 'Identifying Objects',
          items: [
            'Can ask "What is this?" and understand answers',
            'Can point to objects and name them',
            'Can describe objects with basic adjectives',
            'Can ask about unfamiliar objects'
          ]
        },
        {
          category: 'Personal Information',
          items: [
            'Can answer "Where do you live?"',
            'Can respond to "What\'s your name?"',
            'Can give basic information about myself',
            'Can ask others for basic personal information'
          ]
        },
        {
          category: 'Numbers & Dates',
          items: [
            'Can count from 1-100',
            'Can say days of the week',
            'Can say months of the year',
            'Can tell basic time (o\'clock)',
            'Can understand and use basic dates'
          ]
        },
        {
          category: 'Simple Descriptions',
          items: [
            'Can describe my home in simple words',
            'Can talk about my school/work simply',
            'Can describe my free time activities',
            'Can use basic adjectives to describe things'
          ]
        }
      ]
    },
    'a2': {
      title: 'A2 Level (Elementary/Waystage)',
      description: 'At the A2 level, learners can handle simple information and express themselves in familiar contexts, expanding on basic abilities.',
      color: 'from-blue-400 to-cyan-500',
      topics: [
        {
          title: 'Personal & Family Information',
          description: 'Understanding and discussing basic personal and family details',
          items: [
            'Can describe my family in detail',
            'Can talk about family relationships',
            'Can discuss family traditions',
            'Can ask about others\' families',
            'Can share personal background information'
          ]
        },
        {
          title: 'Shopping',
          description: 'Communicating about shopping needs and making basic purchases',
          items: [
            'Can ask for prices in shops',
            'Can make simple purchases',
            'Can ask about product availability',
            'Can understand basic shopping vocabulary',
            'Can handle money and payment conversations'
          ]
        },
        {
          title: 'Local Geography & Employment',
          description: 'Discussing local areas and basic employment information',
          items: [
            'Can describe my neighborhood',
            'Can give directions to familiar places',
            'Can talk about my job/studies',
            'Can discuss local transportation',
            'Can describe local attractions'
          ]
        },
        {
          title: 'Routine Tasks',
          description: 'Communicating in simple and routine tasks requiring a direct exchange of information',
          items: [
            'Can make appointments',
            'Can order food in restaurants',
            'Can book hotel rooms',
            'Can handle bank transactions',
            'Can communicate with service providers'
          ]
        },
        {
          title: 'Daily Activities & Preferences',
          description: 'Following short conversations about routines and preferences',
          items: [
            'Can describe my daily routine',
            'Can talk about likes and dislikes',
            'Can discuss hobbies and interests',
            'Can plan simple activities with others',
            'Can compare preferences'
          ]
        },
        {
          title: 'Simple Descriptions',
          description: 'Describing aspects of their background, immediate environment, and immediate needs in simple terms',
          items: [
            'Can describe my education background',
            'Can talk about my hometown',
            'Can express immediate needs clearly',
            'Can describe my living situation',
            'Can talk about my goals'
          ]
        },
        {
          title: 'Short Messages & Stories',
          description: 'Understanding messages from social media/email and simple stories',
          items: [
            'Can understand social media posts',
            'Can read simple emails',
            'Can follow basic news stories',
            'Can understand simple narratives',
            'Can write basic social media updates'
          ]
        }
      ],
      vocabulary: [
        {
          category: 'Asking for Directions',
          items: [
            'Can ask "How do I get to...?"',
            'Can understand "Is it far?"',
            'Can follow simple directions',
            'Can use basic direction vocabulary (left, right, straight)'
          ]
        },
        {
          category: 'Describing Daily Life',
          items: [
            'Can answer "What do you do every day?"',
            'Can use "I like to..." in conversations',
            'Can describe typical weekdays and weekends',
            'Can talk about daily habits'
          ]
        },
        {
          category: 'Food & Hobbies',
          items: [
            'Can name common foods and drinks',
            'Can describe cooking methods',
            'Can discuss favorite foods',
            'Can talk about sports and hobbies',
            'Can describe travel experiences'
          ]
        },
        {
          category: 'Simple Narratives',
          items: [
            'Can describe familiar scenes using basic words',
            'Can tell simple stories about past events',
            'Can describe pictures and scenes',
            'Can narrate daily events in sequence'
          ]
        },
        {
          category: 'Responding to Inquiries',
          items: [
            'Can handle basic work-related questions',
            'Can respond to straightforward requests',
            'Can ask for clarification when needed',
            'Can provide basic information about services'
          ]
        }
      ]
    },
    'b1': {
      title: 'B1 Level (Intermediate/Threshold)',
      description: 'The B1 level marks the beginning of independent language use, allowing learners to deal with most situations encountered in daily life.',
      color: 'from-yellow-400 to-orange-500',
      topics: [
        {
          title: 'Travel',
          description: 'Dealing with most situations likely to arise while traveling in an area where English is spoken',
          items: [
            'Can handle airport and hotel check-ins',
            'Can ask for tourist information',
            'Can deal with travel problems',
            'Can book tours and activities',
            'Can navigate public transportation',
            'Can handle emergency situations while traveling'
          ]
        },
        {
          title: 'Familiar & Personal Interest Topics',
          description: 'Producing simple connected text and entering unprepared into conversations on familiar topics',
          items: [
            'Can discuss family relationships in detail',
            'Can talk about career aspirations',
            'Can discuss current events and news',
            'Can share opinions on entertainment',
            'Can debate simple social issues',
            'Can discuss educational experiences'
          ]
        },
        {
          title: 'Experiences & Events',
          description: 'Describing experiences, events, dreams, hopes, and ambitions',
          items: [
            'Can narrate past experiences with detail',
            'Can describe significant life events',
            'Can talk about future plans and dreams',
            'Can discuss personal goals and ambitions',
            'Can share memorable experiences',
            'Can describe emotional experiences'
          ]
        },
        {
          title: 'Opinions & Plans',
          description: 'Briefly giving reasons and explanations for opinions and plans',
          items: [
            'Can justify personal opinions',
            'Can explain decision-making processes',
            'Can compare different options',
            'Can discuss pros and cons',
            'Can present alternative viewpoints',
            'Can negotiate simple plans'
          ]
        },
        {
          title: 'Media Comprehension',
          description: 'Understanding the main points of many radio or TV programs on current affairs or topics of personal/professional interest',
          items: [
            'Can follow news broadcasts',
            'Can understand documentary content',
            'Can follow podcast discussions',
            'Can comprehend interview content',
            'Can understand TV show plots',
            'Can follow radio talk shows'
          ]
        },
        {
          title: 'Personal Correspondence',
          description: 'Understanding and writing simple personal letters, notes, emails, and text messages',
          items: [
            'Can write detailed personal emails',
            'Can compose formal letters',
            'Can write invitation letters',
            'Can respond to complex inquiries',
            'Can write complaint letters',
            'Can maintain email correspondence'
          ]
        }
      ],
      vocabulary: [
        {
          category: 'Narrating',
          items: [
            'Can respond to "What happened next?"',
            'Can use "Tell me about..." effectively',
            'Can use past tense forms accurately',
            'Can sequence events clearly',
            'Can use time expressions properly'
          ]
        },
        {
          category: 'Expressing Opinions',
          items: [
            'Can use "I think that..." naturally',
            'Can express "In my opinion..." effectively',
            'Can agree and disagree politely',
            'Can show uncertainty with appropriate language',
            'Can modify opinions when presented with new information'
          ]
        },
        {
          category: 'Discussing Media',
          items: [
            'Can talk about TV shows with detail',
            'Can discuss podcast content',
            'Can analyze news articles',
            'Can compare different media sources',
            'Can express preferences about media consumption'
          ]
        },
        {
          category: 'Problem Solving',
          items: [
            'Can make opinions clear when finding solutions',
            'Can participate in group problem-solving',
            'Can suggest practical alternatives',
            'Can evaluate different solutions',
            'Can reach consensus in group discussions'
          ]
        },
        {
          category: 'Presentations',
          items: [
            'Can give short prepared presentations',
            'Can answer questions about presentation topics',
            'Can use visual aids effectively',
            'Can structure presentations logically',
            'Can engage audience during presentations'
          ]
        }
      ]
    },
    'b2': {
      title: 'B2 Level (Upper-Intermediate/Vantage)',
      description: 'At the B2 level, learners demonstrate a greater degree of independence and fluency, capable of handling more complex communication.',
      color: 'from-purple-400 to-pink-500',
      topics: [
        {
          title: 'Complex Texts & Discussions',
          description: 'Understanding the main ideas of complex text on both concrete and abstract topics, including technical discussions',
          items: [
            'Can understand academic articles in my field',
            'Can follow complex arguments in discussions',
            'Can comprehend technical manuals',
            'Can understand abstract concepts in texts',
            'Can analyze argumentative essays',
            'Can understand specialized vocabulary in context'
          ]
        },
        {
          title: 'Fluent Interaction',
          description: 'Interacting with a degree of fluency and spontaneity that makes regular interaction with native speakers quite possible',
          items: [
            'Can maintain conversations without strain',
            'Can participate in group discussions naturally',
            'Can handle unexpected topics in conversation',
            'Can understand cultural references',
            'Can use humor appropriately',
            'Can manage informal and formal registers'
          ]
        },
        {
          title: 'Detailed Descriptions & Viewpoints',
          description: 'Producing clear, detailed text on a wide range of subjects and explaining viewpoints on topical issues',
          items: [
            'Can write detailed reports',
            'Can create comprehensive proposals',
            'Can analyze advantages and disadvantages',
            'Can present balanced arguments',
            'Can synthesize information from multiple sources',
            'Can write persuasive essays'
          ]
        },
        {
          title: 'Contemporary Issues',
          description: 'Reading articles and reports concerned with contemporary problems where writers adopt particular attitudes or viewpoints',
          items: [
            'Can understand editorial opinions',
            'Can analyze different perspectives',
            'Can identify bias in articles',
            'Can understand satirical content',
            'Can follow political discussions',
            'Can comprehend social commentary'
          ]
        },
        {
          title: 'Extended Discussions',
          description: 'Following the main points of extended discussions on everyday familiar topics and identifying important information',
          items: [
            'Can follow lengthy debates',
            'Can identify key points in discussions',
            'Can understand implied meanings',
            'Can follow rapid speech in discussions',
            'Can distinguish main and supporting ideas',
            'Can understand speaker relationships and attitudes'
          ]
        },
        {
          title: 'Formal Writing',
          description: 'Writing essays or reports, passing on information or giving reasons in support of or against a particular point of view',
          items: [
            'Can write structured academic essays',
            'Can compose business reports',
            'Can write formal complaints and responses',
            'Can create detailed proposals',
            'Can write research summaries',
            'Can compose recommendation letters'
          ]
        }
      ],
      vocabulary: [
        {
          category: 'Debates & Discussions',
          items: [
            'Can engage in complex topic debates',
            'Can participate in structured academic discussions',
            'Can moderate group discussions',
            'Can challenge others\' viewpoints respectfully',
            'Can synthesize multiple perspectives'
          ]
        },
        {
          category: 'Articulating Opinions',
          items: [
            'Can express nuanced opinions clearly',
            'Can present multiple viewpoints',
            'Can use sophisticated opinion language',
            'Can hedge opinions appropriately',
            'Can express degrees of certainty'
          ]
        },
        {
          category: 'Analyzing Information',
          items: [
            'Can interpret complex graphs and charts',
            'Can synthesize information from multiple texts',
            'Can identify patterns in data',
            'Can draw conclusions from evidence',
            'Can compare and contrast information sources'
          ]
        },
        {
          category: 'Presenting Arguments',
          items: [
            'Can present and defend arguments in interviews',
            'Can structure logical arguments',
            'Can anticipate counterarguments',
            'Can use evidence to support claims',
            'Can present arguments persuasively'
          ]
        },
        {
          category: 'Professional Communication',
          items: [
            'Can participate in academic discussions confidently',
            'Can handle professional presentations',
            'Can negotiate in business contexts',
            'Can write professional correspondence',
            'Can participate in formal meetings'
          ]
        }
      ]
    },
    'c1': {
      title: 'C1 Level (Advanced/Effective Operational Proficiency)',
      description: 'The C1 level signifies advanced proficiency, enabling effective and flexible language use for complex purposes.',
      color: 'from-red-400 to-rose-500',
      topics: [
        {
          title: 'Demanding Texts & Implicit Meaning',
          description: 'Understanding a wide range of demanding, longer texts and recognizing implicit meaning',
          items: [
            'Can understand literary works and their themes',
            'Can comprehend complex academic papers',
            'Can interpret subtle implications in texts',
            'Can understand idiomatic expressions in context',
            'Can follow complex legal or technical documents',
            'Can understand cultural and historical references'
          ]
        },
        {
          title: 'Fluent & Spontaneous Expression',
          description: 'Expressing ideas fluently and spontaneously without much obvious searching for expressions',
          items: [
            'Can speak spontaneously in any situation',
            'Can use complex grammatical structures naturally',
            'Can self-correct without interrupting flow',
            'Can vary language style according to context',
            'Can use discourse markers effectively',
            'Can maintain fluency even on unfamiliar topics'
          ]
        },
        {
          title: 'Flexible Language Use',
          description: 'Using language flexibly and effectively for social, academic, and professional purposes',
          items: [
            'Can adapt register to different audiences',
            'Can use language for complex social functions',
            'Can handle academic presentations expertly',
            'Can participate in high-level professional meetings',
            'Can negotiate complex business deals',
            'Can mediate between different parties'
          ]
        },
        {
          title: 'Complex Written Communication',
          description: 'Producing clear, well-structured, detailed text on complex subjects with controlled organizational patterns',
          items: [
            'Can write complex research papers',
            'Can compose sophisticated proposals',
            'Can write detailed analytical reports',
            'Can create compelling argumentative essays',
            'Can write professional reviews and critiques',
            'Can compose complex formal correspondence'
          ]
        },
        {
          title: 'Specialized Articles & Instructions',
          description: 'Understanding specialized articles and longer technical instructions, even when not related to their field',
          items: [
            'Can understand specialized medical texts',
            'Can follow complex legal procedures',
            'Can comprehend technical manuals',
            'Can understand academic journals outside my field',
            'Can follow detailed assembly instructions',
            'Can understand complex software documentation'
          ]
        },
        {
          title: 'Discussions & Presentations',
          description: 'Taking an active part in conversations on a wide range of topics and giving detailed descriptions and presentations',
          items: [
            'Can lead complex discussions effectively',
            'Can give sophisticated presentations',
            'Can handle Q&A sessions expertly',
            'Can facilitate group problem-solving',
            'Can present complex ideas clearly',
            'Can engage in high-level academic discourse'
          ]
        }
      ],
      vocabulary: [
        {
          category: 'Academic & Professional Language',
          items: [
            'Can use advanced vocabulary from academic materials',
            'Can employ professional terminology accurately',
            'Can understand and use complex grammatical structures',
            'Can use sophisticated connectors and discourse markers',
            'Can employ precise vocabulary for specific contexts'
          ]
        },
        {
          category: 'In-depth Analysis',
          items: [
            'Can engage in discussion groups requiring deep analysis',
            'Can provide detailed arguments with evidence',
            'Can analyze complex problems systematically',
            'Can evaluate multiple perspectives critically',
            'Can synthesize complex information effectively'
          ]
        },
        {
          category: 'Formulating Ideas',
          items: [
            'Can formulate ideas with precision',
            'Can relate contributions skillfully to others',
            'Can build on others\' ideas effectively',
            'Can express abstract concepts clearly',
            'Can use language creatively and flexibly'
          ]
        },
        {
          category: 'Writing Academic Essays',
          items: [
            'Can write well-structured academic essays',
            'Can use appropriate academic style and tone',
            'Can cite sources correctly',
            'Can develop complex arguments logically',
            'Can write compelling conclusions'
          ]
        },
        {
          category: 'Nuanced Conversations',
          items: [
            'Can engage in nuanced conversations',
            'Can participate in academic discussions',
            'Can understand and use subtle language',
            'Can pick up on implicit meanings',
            'Can use language diplomatically'
          ]
        }
      ]
    },
    'c2': {
      title: 'C2 Level (Proficiency/Mastery)',
      description: 'The C2 level represents mastery, indicating near-native fluency and the ability to handle virtually any communicative situation.',
      color: 'from-indigo-400 to-purple-600',
      topics: [
        {
          title: 'Comprehensive Understanding',
          description: 'Understanding with ease virtually everything heard or read',
          items: [
            'Can understand any spoken English effortlessly',
            'Can comprehend complex literary works',
            'Can follow rapid, colloquial speech',
            'Can understand all accents and dialects',
            'Can comprehend implicit and explicit meaning',
            'Can understand humor, irony, and sarcasm'
          ]
        },
        {
          title: 'Summarizing & Reconstructing',
          description: 'Summarizing information from different spoken and written sources, reconstructing arguments and accounts',
          items: [
            'Can synthesize information from multiple sources',
            'Can reconstruct complex arguments coherently',
            'Can create comprehensive summaries',
            'Can identify and reconcile contradictory information',
            'Can present information in new formats',
            'Can create original interpretations of source material'
          ]
        },
        {
          title: 'Highly Fluent & Precise Expression',
          description: 'Expressing themselves spontaneously, very fluently, and precisely, differentiating finer shades of meaning',
          items: [
            'Can express subtle distinctions in meaning',
            'Can use language with complete naturalness',
            'Can employ stylistic devices effectively',
            'Can convey precise shades of meaning',
            'Can adapt style to any context instantly',
            'Can use language with native-like precision'
          ]
        },
        {
          title: 'Complex Discussions & Debates',
          description: 'Understanding discussions and debates on abstract, complex, or unfamiliar topics in detail',
          items: [
            'Can follow highly complex debates',
            'Can understand abstract philosophical discussions',
            'Can comprehend specialized academic debates',
            'Can identify implied attitudes and opinions',
            'Can understand complex political discourse',
            'Can follow expert-level technical discussions'
          ]
        },
        {
          title: 'Sophisticated Content',
          description: 'Consuming literature, academic journals, and professional reports',
          items: [
            'Can analyze literary works critically',
            'Can understand complex academic research',
            'Can comprehend detailed professional reports',
            'Can understand historical and cultural texts',
            'Can follow complex legal documents',
            'Can understand sophisticated journalistic writing'
          ]
        },
        {
          title: 'Complex Written Works',
          description: 'Writing complex letters, reports, or articles with effective logical structure',
          items: [
            'Can write sophisticated analytical papers',
            'Can compose complex business proposals',
            'Can write compelling creative pieces',
            'Can produce detailed research reports',
            'Can write professional reviews and critiques',
            'Can compose complex formal documents'
          ]
        }
      ],
      vocabulary: [
        {
          category: 'Subtle Nuances',
          items: [
            'Can differentiate subtle shades of meaning',
            'Can understand and use idiomatic expressions naturally',
            'Can recognize and use colloquialisms appropriately',
            'Can understand regional variations in language',
            'Can use language with native-like subtlety'
          ]
        },
        {
          category: 'Emotional & Allusive Language',
          items: [
            'Can use emotional language flexibly',
            'Can employ allusive language effectively',
            'Can use joking language appropriately',
            'Can understand and use metaphorical language',
            'Can employ rhetorical devices skillfully'
          ]
        },
        {
          category: 'High-Level Professional/Academic Communication',
          items: [
            'Can communicate at expert level in professional settings',
            'Can participate in high-level academic discourse',
            'Can handle complex negotiations',
            'Can mediate between different parties',
            'Can provide expert consultation'
          ]
        },
        {
          category: 'Articulating Sounds',
          items: [
            'Can articulate virtually all English sounds accurately',
            'Can ensure complete intelligibility',
            'Can use pronunciation for stylistic effect',
            'Can adapt pronunciation to different contexts',
            'Can achieve native-like naturalness'
          ]
        },
        {
          category: 'Integrating Sub-themes',
          items: [
            'Can give detailed descriptions of complex subjects',
            'Can integrate sub-themes effectively',
            'Can develop particular points in detail',
            'Can create coherent presentations on complex topics',
            'Can synthesize multiple complex themes'
          ]
        }
      ]
    }
  }

  const currentLevel = levelData[level as keyof typeof levelData]

  if (!currentLevel) {
    return (
      <div className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Level not found</h1>
          <Link to="/guides" className="text-blue-400 hover:text-blue-300">
            Back to Guides
          </Link>
        </div>
      </div>
    )
  }

  const calculateProgress = () => {
    const totalItems = currentLevel.topics.reduce((acc, topic) => acc + topic.items.length, 0) +
                     currentLevel.vocabulary.reduce((acc, vocab) => acc + vocab.items.length, 0)
    const completedItems = checkedItems.size
    return Math.round((completedItems / totalItems) * 100)
  }

  const CheckboxItem = ({ itemId, children }: { itemId: string; children: React.ReactNode }) => {
    const isChecked = checkedItems.has(itemId)
    
    return (
      <div 
        className={`flex items-start p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
          isChecked 
            ? 'bg-green-500/10 border-green-500/30 text-green-400' 
            : 'bg-slate-800/30 border-slate-700 text-slate-300 hover:border-slate-600'
        }`}
        onClick={() => toggleItem(itemId)}
      >
        <div className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 mt-0.5 flex items-center justify-center ${
          isChecked 
            ? 'bg-green-500 border-green-500' 
            : 'border-slate-600 hover:border-slate-500'
        }`}>
          {isChecked && <CheckCircle className="w-3 h-3 text-white" />}
        </div>
        <span className={`flex-1 ${isChecked ? 'line-through opacity-75' : ''}`}>
          {children}
        </span>
      </div>
    )
  }

  return (
    <div className="px-4 py-12 sm:px-6 lg:px-8 animate-fade-in">
      <div className="mx-auto max-w-4xl">
        {/* Back Navigation */}
        <Link
          to="/guides"
          className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Guides
        </Link>

        {/* Header */}
        <header className="mb-12">
          <div className="mb-6">
            <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${currentLevel.color} rounded-xl mb-4`}>
              <Award className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">{currentLevel.title}</h1>
            <p className="text-xl text-slate-300 mb-6">{currentLevel.description}</p>
          </div>

          {/* Progress Tracker */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <Target className="h-5 w-5 mr-2" />
                Your Progress
              </h3>
              <span className="text-2xl font-bold text-white">{calculateProgress()}%</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-3">
              <div 
                className={`bg-gradient-to-r ${currentLevel.color} h-3 rounded-full transition-all duration-500`}
                style={{ width: `${calculateProgress()}%` }}
              ></div>
            </div>
            <p className="text-slate-400 text-sm mt-2">
              {checkedItems.size} of {
                currentLevel.topics.reduce((acc, topic) => acc + topic.items.length, 0) +
                currentLevel.vocabulary.reduce((acc, vocab) => acc + vocab.items.length, 0)
              } skills completed
            </p>
          </div>
        </header>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-12">
          <a
            href="https://vivalingua.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 group"
          >
            <Play className="h-4 w-4 mr-2" />
            Practice with AI
          </a>
        </div>

        {/* Topics & Situations */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
            <BookOpen className="h-6 w-6 mr-2" />
            Topics & Situations
          </h2>
          <div className="space-y-8">
            {currentLevel.topics.map((topic, topicIndex) => (
              <div key={topicIndex} className="bg-slate-800/30 rounded-xl p-6 border border-slate-700">
                <h3 className="text-xl font-semibold text-white mb-2">{topic.title}</h3>
                <p className="text-slate-300 mb-4">{topic.description}</p>
                <div className="space-y-2">
                  {topic.items.map((item, itemIndex) => (
                    <CheckboxItem 
                      key={`topic-${topicIndex}-${itemIndex}`} 
                      itemId={`topic-${topicIndex}-${itemIndex}`}
                    >
                      {item}
                    </CheckboxItem>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Common Vocabulary/Questions & Answers */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6">Common Vocabulary & Communication Skills</h2>
          <div className="space-y-8">
            {currentLevel.vocabulary.map((vocab, vocabIndex) => (
              <div key={vocabIndex} className="bg-slate-800/30 rounded-xl p-6 border border-slate-700">
                <h3 className="text-xl font-semibold text-white mb-4">{vocab.category}</h3>
                <div className="space-y-2">
                  {vocab.items.map((item, itemIndex) => (
                    <CheckboxItem 
                      key={`vocab-${vocabIndex}-${itemIndex}`} 
                      itemId={`vocab-${vocabIndex}-${itemIndex}`}
                    >
                      {item}
                    </CheckboxItem>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Next Steps */}
        <section className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-8 border border-blue-500/30">
          <h2 className="text-2xl font-semibold text-white mb-4">Ready to Practice?</h2>
          <p className="text-slate-300 mb-6">
            Use the checklist above to track your progress and practice these skills with VivaLingua's AI conversation system.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://vivalingua.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 group"
            >
              Start Practicing Now
              <Play className="ml-2 h-4 w-4" />
            </a>
            <Link
              to="/guides"
              className="inline-flex items-center px-6 py-3 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200"
            >
              Browse Other Guides
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}

export default LevelGuidePage